import {
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { FormikErrors } from "formik";
import {
  FERTILIZER_TYPES,
  NITROGEN_FERTILIZER_TYPE,
  ORGANIC_FERTILIZER_ANIMAL_TYPES,
  ORGANIC_FERTILIZER_APPLICATION_METHOD_OPTIONS_BY_TYPE,
  ORGANIC_FERTILIZER_TYPES,
} from "model";

import {
  AnyOtherFertilizerEvent,
  FertilizerEventFormData,
  LimeApplicationEvent,
  OrganicFertilizerApplicationEvent,
  SyntheticFertilizerApplicationEvent,
} from "./FertilizerEventFormData";

interface Props {
  fertilizerProperties:
    | OrganicFertilizerApplicationEvent
    | SyntheticFertilizerApplicationEvent
    | LimeApplicationEvent
    | AnyOtherFertilizerEvent;
  handleInputChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: string
  ) => Promise<void>;
  errors?: FormikErrors<FertilizerEventFormData>;
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  fertilizerType: FERTILIZER_TYPES;
}

export default function EnterFertilizerEventFertilizerProperties({
  fertilizerType,
  fertilizerProperties,
  handleInputChange,
  errors,
  setFieldValue,
}: Props) {
  const { t } = useTranslation();

  if (fertilizerType === FERTILIZER_TYPES.ORGANIC) {
    const organic = fertilizerProperties as OrganicFertilizerApplicationEvent;
    const organicErrors =
      errors?.fertilizerProperties as OrganicFertilizerApplicationEvent;

    // Application methods depend on the organic fertiliser type (solid FYM,
    // liquid slurry, dirty water all have different realistic methods).
    const applicationMethodOptions = organic.organicFertilizerType
      ? ORGANIC_FERTILIZER_APPLICATION_METHOD_OPTIONS_BY_TYPE[
          organic.organicFertilizerType
        ]
      : [];

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            required
            value={organic.organicFertilizerType ?? ""}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.organicFertilizerType`
              );
              // Reset the application method when it isn't valid for the
              // newly selected fertiliser type.
              const nextType = event.target.value as ORGANIC_FERTILIZER_TYPES;
              const nextMethods =
                ORGANIC_FERTILIZER_APPLICATION_METHOD_OPTIONS_BY_TYPE[
                  nextType
                ] ?? [];
              if (
                organic.applicationMethod &&
                !nextMethods.includes(organic.applicationMethod)
              ) {
                setFieldValue("fertilizerProperties.applicationMethod", "");
              }
            }}
            error={!!organicErrors?.organicFertilizerType}
            label={t("fertilizerEvent.fertilizerProperties.organicType.text")}
            name={`fertilizerProperties.organicFertilizerType`}
          >
            {Object.entries(ORGANIC_FERTILIZER_TYPES)
              .sort(([, a], [, b]) => a.localeCompare(b))
              .map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {t(`fertilizerEvent.organicFertilizerType.label.${value}`)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={organic?.isImported ?? false}
                sx={{
                  "&.Mui-checked": { color: "#7EC254" },
                }}
              />
            }
            label={t("organicFertilizer.imported.label")}
            name={`fertilizerProperties.isImported`}
            onChange={(event) => {
              setFieldValue(
                "fertilizerProperties.isImported",
                (event.target as HTMLInputElement).checked,
                false
              );
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            required
            value={organic.animalType ?? []}
            onChange={async (e) =>
              await handleInputChange(e, "fertilizerProperties.animalType")
            }
            error={!!organicErrors?.animalType}
            label={t("fertilizerEventInput.animalType.label")}
            name={"fertilizerProperties.animalType"}
            SelectProps={{
              multiple: true,
              renderValue: (selected) =>
                (selected as ORGANIC_FERTILIZER_ANIMAL_TYPES[])
                  .map((m) => t(`fertilizerEventInput.animalType.options.${m}`))
                  .join(", "),
            }}
          >
            {Object.keys(ORGANIC_FERTILIZER_ANIMAL_TYPES).map((key) => (
              <MenuItem key={key} value={key}>
                <Checkbox
                  checked={(organic.animalType ?? []).includes(
                    key as ORGANIC_FERTILIZER_ANIMAL_TYPES
                  )}
                  size="small"
                />
                <ListItemText
                  primary={t(`fertilizerEventInput.animalType.options.${key}`)}
                />
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            required
            value={organic.applicationMethod ?? ""}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.applicationMethod`
              );
            }}
            error={!!organicErrors?.applicationMethod}
            disabled={!organic.organicFertilizerType}
            label={t("fertilizerProperties.applicationMethod")}
            name={`fertilizerProperties.applicationMethod`}
          >
            {applicationMethodOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {t(
                  `fertilizerEvent.fertilizerProperties.applicationMethod.options.${value}`
                )}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    );
  }

  if (fertilizerType === FERTILIZER_TYPES.SYNTHETIC) {
    const synthetic =
      fertilizerProperties as SyntheticFertilizerApplicationEvent;
    const syntheticErrors =
      errors?.fertilizerProperties as SyntheticFertilizerApplicationEvent;

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            required
            value={synthetic.typeOfNitrogen ?? ""}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.typeOfNitrogen`
              );
            }}
            error={!!syntheticErrors?.typeOfNitrogen}
            label={t(
              "fertilizerEvent.fertilizerProperties.typeOfNitrogen.text"
            )}
            name={`fertilizerProperties.typeOfNitrogen`}
          >
            {Object.entries(NITROGEN_FERTILIZER_TYPE)
              .sort(([, a], [, b]) => a.localeCompare(b))
              .map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {t(`fertilizerEvent.typeOfNitrogen.label.${value}`)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            value={synthetic.productName}
            type="text"
            id="productName"
            error={!!syntheticErrors?.productName}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.productName`
              );
            }}
            label={t("fertilizerEvent.fertilizerProperties.productName.label")}
            name={`fertilizerProperties.productName`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            required
            value={synthetic.nPercentage}
            type="number"
            id="nPercentage"
            error={!!syntheticErrors?.nPercentage}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.nPercentage`
              );
            }}
            label={t("fertilizerEvent.fertilizerProperties.nPercentage.label")}
            name={`fertilizerProperties.nPercentage`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            value={synthetic.pPercentage}
            type="number"
            id="pPercentage"
            error={!!syntheticErrors?.pPercentage}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.pPercentage`
              );
            }}
            label={t("fertilizerEvent.fertilizerProperties.pPercentage.label")}
            name={`fertilizerProperties.pPercentage`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            value={synthetic.kPercentage}
            type="number"
            id="kPercentage"
            error={!!syntheticErrors?.kPercentage}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.kPercentage`
              );
            }}
            label={t("fertilizerEvent.fertilizerProperties.kPercentage.label")}
            name={`fertilizerProperties.kPercentage`}
          />
        </Grid>
      </Grid>
    );
  }

  if (fertilizerType === FERTILIZER_TYPES.LIME) {
    const lime = fertilizerProperties as LimeApplicationEvent;
    const limeErrors = errors?.fertilizerProperties as LimeApplicationEvent;

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            required
            value={lime.phPriorToApplication}
            type="number"
            id="phPriorToApplication"
            error={!!limeErrors?.phPriorToApplication}
            onChange={async (event) => {
              await handleInputChange(
                event,
                `fertilizerProperties.phPriorToApplication`
              );
            }}
            label={t(
              "fertilizerEvent.fertilizerProperties.phPriorToApplication.label"
            )}
            name={`fertilizerProperties.phPriorToApplication`}
          />
        </Grid>
      </Grid>
    );
  }
  return <Grid></Grid>;
}
